import { vh } from './utils/constants'

const $ = jQuery

export class Slide {
    constructor(slideID, slideType = null, slideTransition = null) {
        this._slideID = slideID
        this._slide = document.querySelector(`#${this._slideID}`)
        this._slideType = slideType ?? Slide.extractType(this._slide)
        this._slideTransition = slideTransition ?? Slide.extractTransition(this._slide)
        this._slideImagesTransition = Slide.extractImagesTransition(this._slide)
        this._animationTransitionGap = 10 // Gap between the end of the slide animation and the transition to the next slide (in percentage of viewport height)
    }

    static extractType(slideEl) {
        const match = slideEl?.classList.value.match(/slide--([a-zA-Z0-9_]+)/)
        return match ? match[1] : null
    }

    static extractTransition(slideEl) {
        const match = slideEl?.classList.value.match(/slide-transition--([a-zA-Z0-9_]+)/)
        return match ? match[1] : null
    }

    static extractImagesTransition(slideEl) {
        const slideImages = slideEl.querySelector('.slide-images')
        const match = slideImages?.classList.value.match(/slide-images-transition--([a-zA-Z0-9_]+)/)
        return match ? match[1] : null
    }

    get slideID() {
        return this._slideID
    }
    get slide() {
        return this._slide
    }
    get slideType() {
        return this._slideType
    }
    get slideTransition() {
        return this._slideTransition
    }

    get slideImagesTransition() {
        return this._slideImagesTransition
    }

    get animationTransitionGap() {
        return this._animationTransitionGap
    }

    set animationTransitionGap(value) {
        if (typeof value === 'number' && value >= 0) {
            this._animationTransitionGap = value
        } else {
            console.warn(
                'Invalid value for animationTransitionGap. It must be a non-negative number.'
            )
        }
    }

    init() {
        this.transitionNext()
    }

    transitionNext(args) {
        switch (this.slideTransition) {
            case 'fade':
                return this.transitionFade(args)
            case 'slide':
                return this.transitionSlide(args)
            default:
                console.warn(
                    `No transition defined for slide type: ${this.slideType}, transition: ${this.slideTransition}`
                )
                return null
        }
    }

    transitionFade(args) {
        return ScrollTrigger.create({
            trigger: this.slide,
            start: 'top top-=1px', // Offset by -1px so it gets triggered just after pinWhileAnimating ends
            pin: true,
            pinSpacing: false,
            animation: gsap.to(this.slide, {
                autoAlpha: 0,
                duration: 2,
            }),
            toggleActions: 'play none none reverse',
            onEnter: () => {
                // The animation is not being triggered when the slide enters for the second time, so we need to trigger it manually.
                gsap.to(this.slide, {
                    autoAlpha: 0,
                    duration: 2,
                })
            },
            ...args,
        })
    }

    transitionSlide(args) {
        return ScrollTrigger.create({
            trigger: this.slide,
            start: 'top top-=1px', // Offset by -1px so it gets triggered just after pinWhileAnimating ends
            end: '+=100%',
            pin: true,
            pinSpacing: false,
            ...args,
        })
    }

    pinWhileAnimating(args) {
        return ScrollTrigger.create({
            trigger: this.slide,
            start: 'top top',
            end: `+=${this.calculateAnimationDuration() + this.animationTransitionGap}%`,
            pin: true,
            ...args,
        })
    }

    triggerAnimation(args) {
        return ScrollTrigger.create({
            trigger: this.slide,
            start: 'top top+=1px',
            end: '+=100%',
            scrub: 2,
            ...args,
        })
    }

    getPreviousSlide() {
        const previousSlide =
            this.slide.previousElementSibling || this.slide.parentElement.previousElementSibling
        if (previousSlide) {
            if (previousSlide.classList.contains('slide')) {
                return previousSlide
            } else if (previousSlide.classList.contains('pin-spacer')) {
                return previousSlide.querySelector('.slide')
            }
        }

        return null // No previous slide found
    }

    /**
     * Calculates the duration of the animation to be used in the ScrollTrigger.
     *
     * @returns {number} The duration of the animation in percentage of the viewport height.
     */
    calculateAnimationDuration() {
        return 100
    }

    preloadVideo() {
        if (!this.slideVideo) return

        const request = new XMLHttpRequest()
        let videoURL

        request.onload = () => {
            this.slideVideo.src = URL.createObjectURL(request.response)
            this.slideVideo.load()
        }

        videoURL = this.getPlayableVideoUrl()

        request.open('GET', videoURL)
        request.responseType = 'blob'
        request.send()
    }

    getPlayableVideoUrl() {
        if (!this.slideVideo) return

        const sources = this.slideVideo.querySelectorAll('source')
        let selectedSource

        // Find the first source that matches the media query
        for (const source of sources) {
            const mediaAttr = source.getAttribute('media')
            if (!mediaAttr || window.matchMedia(mediaAttr).matches) {
                selectedSource = source
                break
            }
        }

        return selectedSource?.src
    }
}

export class OnlyTextSlide extends Slide {
    init() {
        this.transitionNext()
    }
}

export class ImageTextSlide extends Slide {
    init() {
        const animationDuration = this.calculateAnimationDuration()

        this.pinWhileAnimating()
        this.transitionNext()
        this.triggerAnimation({
            animation: this.createAnimation(),
            end: `+=${animationDuration}%`,
        })
    }

    calculateAnimationDuration() {
        const slideImages = this.slide.querySelector('.slide-images')
        const imageCount = slideImages?.querySelectorAll('img, figure').length || 1
        return 100 + imageCount * 75 // 100% base + 75% for each image
    }

    createAnimation() {
        const tl = gsap.timeline()

        const slideText = this.slide.querySelector('.slide-text')
        const slideImage = this.slide.querySelector('.slide-image')
        const slideImages = this.slide.querySelector('.slide-images')
        const slideTextAnimationDuration =
            1 + (slideImages?.querySelectorAll('img, figure').length || 1) * 1.5 // 1s base + 1.5x for each additional image
        const offsetThreshold = 50 // For some reason, the text is not fully hidden when it hits the bottom of the screen, so add some extra padding.

        tl.fromTo(
            slideText,
            {
                x: '-50%',
                y: (index, target, targets) => {
                    return Math.min(vh / 2, target.offsetHeight) // For some reason, offsetHeight is miscalculated sometimes, so use vh/2 as a fallback
                },
            },
            {
                duration: slideTextAnimationDuration,
                ease: 'slow(0.1, 0.4, false)',
                x: '-50%',
                y: (index, target, targets) => {
                    return -(vh + Math.min(target.offsetHeight, vh / 2))
                },
            }
        )

        if (slideImages) {
            // Transicion between multiple images
            this.transitionNextImages(tl, slideImages.querySelectorAll('img, figure'))
        } else {
            // Single image: Do nothing
        }

        return tl
    }

    transitionNextImages(tl, images) {
        switch (this.slideImagesTransition) {
            case 'slide_horizontal':
                console.log('transitionNextImages: slide_horizontal')
            case 'crossfade':
                this.transitionImagesCrossfade(tl, images)
                break
            default:
                console.warn(
                    `No image transition defined for slide type: ${this.slideType}, transition: ${this.slideImagesTransition}`
                )
        }
    }

    transitionImagesCrossfade(tl, images) {
        images.forEach((image, index) => {
            if (index > 0) {
                tl.to(image, {
                    autoAlpha: 1,
                    duration: 1,
                })
            }
        })
    }
}

export class VideoTextSlide extends Slide {
    init() {
        this.slideText = this.slide.querySelector('.slide-text')
        this.slideVideo = this.slide.querySelector('.slide-video > video')
        if (!this.slideText || !this.slideVideo) {
            console.warn(
                `Slide ${this.slideID} is missing required elements: slide-text or slide-video`
            )
            return
        }
        const animationDuration = this.calculateAnimationDuration()

        this.preloadVideo()

        this.pinWhileAnimating()
        this.transitionNext()
        this.triggerAnimation({
            animation: this.createAnimation(),
            end: `+=${animationDuration}%`,
            scrub: true,
            ease: 'none',
        })
    }

    calculateAnimationDuration() {
        const baseDuration = 100 // Base duration for the scrubbed animation
        const videoDuration = this.slideVideo.duration || 10 // Fallback to 10 second if video duration is not available
        const multiplier = 50 // Duration multiplier for each second of video
        return baseDuration + videoDuration * multiplier
    }

    createAnimation() {
        const tl = gsap.timeline()
        const slideTextAnimationDuration = 1.5
        const slideVideoAnimationDuration = 10

        tl.fromTo(
            this.slideText,
            {
                y: '100%',
                x: '-50%',
            },
            {
                duration: slideTextAnimationDuration,
                ease: 'slow(0.1, 0.4, false)',
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight)
                },
            }
        )

        if (this.slideVideo.readyState >= 1) {
            this.videoScrub(tl, slideVideoAnimationDuration)
        } else {
            this.slideVideo.addEventListener(
                'loadedmetadata',
                () => {
                    this.videoScrub(tl, slideVideoAnimationDuration)
                },
                { once: true }
            )
        }

        return tl
    }

    videoScrub(tl, slideVideoAnimationDuration) {
        tl.fromTo(
            this.slideVideo,
            {
                currentTime: 0,
            },
            {
                currentTime: this.slideVideo.duration || 1,
                duration: slideVideoAnimationDuration,
                ease: 'none',
            },
            '-=0.5'
        )
    }
}

export class CoverSlide extends Slide {
    init() {
        this.slideDates = this.slide.querySelector('.slide-dates')
        this.slideTitleDescription = this.slide.querySelector('.slide-title-description')
        this.slideVideo = this.slide.querySelector('.slide-video > video')
        this.slideOverlay = this.slide.querySelector('.slide-overlay')
        this.animationTransitionGap = 0
        const animationDuration = this.calculateAnimationDuration()

        // Hide and position the slide elements initially
        gsap.set([this.slideDates, this.slideTitleDescription], {
            autoAlpha: 0,
        })
        gsap.set(this.slideTitleDescription, {
            x: '-50%',
        })

        this.preloadVideo()

        this.pinWhileAnimating()
        this.transitionNext()
        this.triggerAnimation({
            animation: this.createAnimation(),
            end: `+=${animationDuration}%`,
            scrub: true,
            ease: 'none',
        })
    }

    calculateAnimationDuration() {
        const baseDuration = 100 // Base duration for the scrubbed animation
        const videoDuration = this.slideVideo.duration || 10 // Fallback to 10 second if video duration is not available
        const multiplier = 50 // Duration multiplier for each second of video
        return baseDuration + videoDuration * multiplier
    }

    calculateSlideVideoAnimationDuration() {
        const baseDuration = 5 // Base duration for the slide video animation
        const videoDuration = this.slideVideo.duration || 10 // Fallback to 10 second if video duration is not available
        const multiplier = 1.75 // Duration multiplier for each second of video
        return baseDuration + videoDuration * multiplier
    }

    createAnimation() {
        const tl = gsap.timeline()
        const slideVideoAnimationDuration = this.calculateSlideVideoAnimationDuration()

        tl.fromTo(
            this.slideDates,
            {
                autoAlpha: 0,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                scale: 1,
            }
        )
            .to(
                this.slideDates,
                {
                    autoAlpha: 0,
                    scale: 1.1,
                },
                '+=2'
            )
            .fromTo(
                this.slideTitleDescription,
                {
                    autoAlpha: 0,
                    y: '-30%',
                },
                {
                    autoAlpha: 1,
                    y: '-50%',
                    duration: 2,
                }
            )
            .to(
                this.slideTitleDescription,
                {
                    autoAlpha: 0,
                    y: '-80%',
                    duration: 2,
                },
                '+=4'
            )
            .to(
                this.slideOverlay,
                {
                    autoAlpha: 0,
                    duration: 2,
                },
                '<+=2'
            )

        if (this.slideVideo.readyState >= 1) {
            this.videoScrub(tl, slideVideoAnimationDuration)
        } else {
            this.slideVideo.addEventListener(
                'loadedmetadata',
                () => {
                    this.videoScrub(tl, slideVideoAnimationDuration)
                },
                { once: true }
            )
        }

        return tl
    }

    videoScrub(tl, slideVideoAnimationDuration) {
        tl.fromTo(
            this.slideVideo,
            {
                currentTime: 0,
            },
            {
                currentTime: this.slideVideo.duration || 1,
                duration: slideVideoAnimationDuration,
                ease: 'none',
            },
            '<'
        )
    }
}

export function createSlide(slideID) {
    const slideEl = document.querySelector(`#${slideID}`)
    if (!slideEl) return null
    const slideType = Slide.extractType(slideEl)
    const slideTransition = Slide.extractTransition(slideEl)
    console.log('createSlide', slideID, slideType, slideTransition)

    switch (slideType) {
        case 'cover':
            return new CoverSlide(slideID, slideType, slideTransition)
        case 'only_text':
            return new OnlyTextSlide(slideID, slideType, slideTransition)
        case 'image_text':
            return new ImageTextSlide(slideID, slideType, slideTransition)
        case 'cover_image_text':
        // return new CoverImageTextSlide(slideID, slideType, slideTransition)
        case 'video_text':
            return new VideoTextSlide(slideID, slideType, slideTransition)
        case 'image_video_text':
        // return new ImageVideoTextSlide(slideID, slideType, slideTransition)
        case 'custom':
        default:
            return new Slide(slideID, slideType, slideTransition)
    }
}
